import React, { useEffect, useState } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ProfileCompletionCard = ({ onCompleteSection, onProfileCompletionUpdate }) => {
  const { userProfile } = useAuth();
  const [completionData, setCompletionData] = useState({
    percentage: 0,
    completedSections: 0,
    totalSections: 8,
    missingSections: []
  });
  
  // Calculate profile completion based on userProfile
  useEffect(() => {
    if (!userProfile) return;
    
    const sections = [
      { 
        id: 'basic_info', 
        name: 'Basic Info',
        fields: ['full_name', 'email', 'phone', 'location', 'bio'],
        requiredCount: 3
      },
      {
        id: 'professional_info',
        name: 'Professional Info',
        fields: ['title', 'experience_years', 'industry_specialization', 'education'],
        requiredCount: 2
      },
      {
        id: 'experience',
        name: 'Work Experience',
        fields: ['work_experience'],
        isArray: true,
        requiredCount: 1
      },
      {
        id: 'portfolio',
        name: 'Portfolio',
        fields: ['work_samples', 'portfolio_url', 'portfolio_description'],
        requiredCount: 1
      },
      {
        id: 'availability',
        name: 'Availability',
        fields: ['availability_status', 'availability_hours', 'timezone'],
        requiredCount: 2
      },
      {
        id: 'rates',
        name: 'Rates',
        fields: ['hourly_rate', 'minimum_rate', 'maximum_rate'],
        requiredCount: 1
      },
      {
        id: 'preferences',
        name: 'Preferences',
        fields: ['preferred_work_type', 'preferred_project_types', 'communication_style'],
        requiredCount: 1
      },
      {
        id: 'profile_image',
        name: 'Profile Picture',
        fields: ['profile_picture'],
        requiredCount: 1
      }
    ];
    
    const completedSectionsList = [];
    const missingSectionNames = [];
    
    // Check each section for completion
    sections.forEach(section => {
      const fieldsCompleted = section.fields.filter(field => {
        if (section.isArray && Array.isArray(userProfile[field])) {
          return userProfile[field].length >= section.requiredCount;
        }
        return userProfile[field] && userProfile[field].toString().trim() !== '';
      }).length;
      
      if (fieldsCompleted >= section.requiredCount) {
        completedSectionsList.push(section);
      } else {
        missingSectionNames.push(section.name);
      }
    });
    
    const percentage = Math.round((completedSectionsList.length / sections.length) * 100);
    
    setCompletionData({
      percentage,
      completedSections: completedSectionsList.length,
      totalSections: sections.length,
      missingSections: missingSectionNames
    });
    
    // Notify parent component of the profile completion percentage
    if (onProfileCompletionUpdate) {
      onProfileCompletionUpdate(percentage);
    }
    
  }, [userProfile, onProfileCompletionUpdate]);

  const getProgressColor = () => {
    if (completionData.percentage < 30) return 'bg-red-500';
    if (completionData.percentage < 70) return 'bg-yellow-500';
    return 'bg-green-500';
  };
  
  const getSectionIcon = (section) => {
    switch (section.toLowerCase()) {
      case 'basic info':
        return 'User';
      case 'skills':
        return 'Award';
      case 'experience':
        return 'Briefcase';
      case 'work experience':
        return 'Briefcase';
      case 'portfolio':
        return 'FolderOpen';
      case 'availability':
        return 'Calendar';
      case 'rates':
        return 'DollarSign';
      default:
        return 'CheckCircle';
    }
  };

  return (
    <div className="card p-4 border-l-4 border-l-warning">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Icon name="User" size={20} className="text-warning" />
          <h3 className="font-semibold text-text-primary">Profile Completion</h3>
        </div>
        <span className="text-sm font-medium text-text-secondary">
          {completionData.completedSections}/{completionData.totalSections} sections
        </span>
      </div>

      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-text-secondary">Progress</span>
          <span className="text-sm font-medium text-text-primary">{completionData.percentage}%</span>
        </div>
        <div className="w-full h-2 bg-secondary-200 rounded-full overflow-hidden">
          <div 
            className={`h-full ${getProgressColor()} transition-all duration-500`}
            style={{ width: `${completionData.percentage}%` }}
          ></div>
        </div>
      </div>

      {completionData.percentage < 100 && (
        <div className="space-y-3">
          <p className="text-sm text-text-secondary">
            Complete these sections to improve your visibility:
          </p>
          
          <div className="space-y-2">
            {completionData.missingSections.slice(0, 3).map((section, index) => (
              <div key={index} className="flex items-center justify-between p-2 bg-secondary-50 rounded-lg">
                <div className="flex items-center space-x-2">
                  <Icon 
                    name={getSectionIcon(section)} 
                    size={16} 
                    className="text-text-muted" 
                  />
                  <span className="text-sm text-text-primary">{section}</span>
                </div>
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => onCompleteSection(section)}
                  iconName="ArrowRight"
                  iconSize={12}
                  className="text-xs"
                >
                  Complete
                </Button>
              </div>
            ))}
          </div>

          {completionData.missingSections.length > 3 && (
            <p className="text-xs text-text-muted">
              +{completionData.missingSections.length - 3} more sections to complete
            </p>
          )}
        </div>
      )}

      {completionData.percentage === 100 && (
        <div className="flex items-center space-x-2 p-3 bg-success-50 rounded-lg">
          <Icon name="CheckCircle" size={20} className="text-success" />
          <div>
            <p className="text-sm font-medium text-success-700">
              Profile Complete!
            </p>
            <p className="text-xs text-success-600">
              Your profile is fully optimized for maximum visibility.
            </p>
          </div>
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Icon name="TrendingUp" size={16} className="text-accent" />
            <span className="text-sm text-text-secondary">Profile Views</span>
          </div>
          <span className="text-sm font-medium text-text-primary">
            {userProfile?.profile_views || 0} this week
            {completionData.percentage > 75 && (
              <span className="text-success ml-1 text-xs">
                +{Math.floor(completionData.percentage / 10)}%
                <Icon name="ArrowUp" size={10} className="inline ml-0.5" />
              </span>
            )}
          </span>
        </div>
        
        {completionData.percentage < 80 && (
          <p className="text-xs text-text-muted mt-2">
            Complete your profile to increase visibility by up to 5x
          </p>
        )}
      </div>
    </div>
  );
};

export default ProfileCompletionCard;

